import {
  assignRoomNumbers,
  buildRoomAdjacencyFromGrid,
  buildRoomNumberingAdjacency,
  buildRoomNumberingGrid,
  buildStaircaseDistanceMap,
  type ICalculatedRoomNumbersByRoomNumber,
  type IRoomNumberingMethodKey,
  type IDoorCalcRoomNumberInput,
  type IRoomForRoomNumberCalculation,
} from "@multiconsult-group/multiplacer-core";
import type { IAppThunk } from "../app/store";
import {
  openapi,
  type PostMultiplacerRoomsUpdateCalculatedRoomNumbersApiResponse,
} from "../../openapi";
import { selectHttpMcbimSpacesResult } from "../McBim/httpMcBimSelectors";
import { selectHttpGetRoomsCurrentFloorResult } from "../Room/httpRoomSelectors";

export const thunkCalculateRoomNumbers =
  (
    methodKey: IRoomNumberingMethodKey
  ): IAppThunk<Promise<PostMultiplacerRoomsUpdateCalculatedRoomNumbersApiResponse>> =>
  async (dispatch, getState) => {
    const state = getState();
    const projectNo = state.design.projectNo;
    const mcbimSpaces = selectHttpMcbimSpacesResult(state);
    const roomsByRoomNumber = selectHttpGetRoomsCurrentFloorResult(state);

    if (!projectNo) {
      return {
        counts: {
          received: 0,
          matched: 0,
          updated: 0,
          unchanged: 0,
          unknown: 0,
        },
        unknownRooms: [],
      };
    }

    const floorName = state.design.floorAndJob?.floorName ?? "";
    const floorNumberMatch = floorName.match(/-(\d+)$/);
    const floorNumber = floorNumberMatch ? parseInt(floorNumberMatch[1]!, 10) : 0;
    const doorItems =
      openapi.endpoints.postMcbimItems.select({
        body: {
          projectNo,
          buildingStoryName: floorName,
          instanceClasses: ["IfcDoor"],
        },
      })(state).data ?? [];

    const doorsOnFloor: IDoorCalcRoomNumberInput[] = doorItems.flatMap((door) => {
      if (!door.globalId || !door.bbox || door.bbox.length < 4) {
        return [];
      }

      return [
        {
          globalId: door.globalId,
          bbox: door.bbox,
        },
      ];
    });

    const roomsOnFloor: IRoomForRoomNumberCalculation[] = mcbimSpaces.flatMap((space) => {
      if (!space.name || !space.polygon || space.polygon.length < 4) {
        return [];
      }

      const roomSummary = roomsByRoomNumber[space.name];

      if (!roomSummary) {
        return [];
      }

      return [
        {
          polygon: space.polygon as IRoomForRoomNumberCalculation["polygon"],
          roomName: roomSummary.roomName,
          roomNumber: roomSummary.roomNumber,
        },
      ];
    });

    const grid = buildRoomNumberingGrid(roomsOnFloor, floorNumber);
    const adj = buildRoomNumberingAdjacency(grid, roomsOnFloor, doorsOnFloor);
    const { distMap, penaltyCostMap } = buildStaircaseDistanceMap(grid, adj, roomsOnFloor);
    const roomAdjTable = buildRoomAdjacencyFromGrid(grid);

    const roomNumberToCalculatedRoomNumber: ICalculatedRoomNumbersByRoomNumber = assignRoomNumbers(
      grid,
      adj,
      distMap,
      penaltyCostMap,
      roomsOnFloor,
      doorsOnFloor,
      roomAdjTable,
      floorNumber
    );

    return dispatch(
      openapi.endpoints.postMultiplacerRoomsUpdateCalculatedRoomNumbers.initiate({
        body: {
          projectNo,
          methodKey,
          roomNumberToCalculatedRoomNumber,
        },
      })
    ).unwrap();
  };
